/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  useColorModeValue,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Progress,
  Code,
  Flex,
  Icon,
  Spinner,
  Select,
} from "@chakra-ui/react";
import {
  FiCpu,
  FiHardDrive,
  FiServer,
  FiActivity,
  FiRefreshCw,
  FiShield,
  FiFileText,
} from "react-icons/fi";
import { useUserRole } from "hooks/useUserRole";
import api from "utils/api";
import { useErrorHandler } from "hooks/useErrorHandler";
import Chart from "react-apexcharts";

export default function SunucuYonetimi() {
  const userRole = useUserRole();
  const { handleError } = useErrorHandler();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedServer, setSelectedServer] = useState("admin"); // "admin" veya "traccar"
  const [adminMetrics, setAdminMetrics] = useState(null);
  const [adminHealth, setAdminHealth] = useState(null);
  const [traccarMetrics, setTraccarMetrics] = useState(null);
  const [traccarHealth, setTraccarHealth] = useState(null);
  const [bannedIPs, setBannedIPs] = useState([]);
  const [errorLogs, setErrorLogs] = useState(null);
  const [accessLogs, setAccessLogs] = useState(null);
  const [adminMetricsHistory, setAdminMetricsHistory] = useState({
    cpu: [],
    memory: [],
    disk: [],
    timestamps: [],
  });

  const cardBg = useColorModeValue("white", "navy.800");
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const fetchData = useCallback(async () => {
    try {
      setRefreshing(true);
      
      // Paralel olarak tüm verileri çek
      const [adminMetricsRes, adminHealthRes, bannedRes, errorLogsRes, accessLogsRes] = await Promise.all([
        api.get("/api/system/metrics").then(res => res.json()),
        api.get("/api/system/health").then(res => res.json()),
        api.get("/api/system/banned-ips?limit=50").then(res => res.json()),
        api.get("/api/system/logs/errors?lines=100").then(res => res.json()),
        api.get("/api/system/logs/access?lines=100").then(res => res.json()),
      ]);

      setAdminMetrics(adminMetricsRes);
      setAdminHealth(adminHealthRes);
      setBannedIPs(bannedRes);
      setErrorLogs(errorLogsRes);
      setAccessLogs(accessLogsRes);

      // Traccar metrikleri şimdilik null (ileride eklenecek)
      setTraccarMetrics(null);
      setTraccarHealth(null);

      // Admin metrik geçmişine ekle (son 20 ölçüm)
      const now = new Date().toLocaleTimeString("tr-TR");
      setAdminMetricsHistory((prev) => {
        const newHistory = {
          cpu: [...prev.cpu.slice(-19), adminMetricsRes.cpu_percent],
          memory: [...prev.memory.slice(-19), adminMetricsRes.memory_percent],
          disk: [...prev.disk.slice(-19), adminMetricsRes.disk_percent],
          timestamps: [...prev.timestamps.slice(-19), now],
        };
        return newHistory;
      });

      setLoading(false);
    } catch (error) {
      handleError(error);
      setLoading(false);
    } finally {
      setRefreshing(false);
    }
  }, [handleError]);

  useEffect(() => {
    // Admin değilse fetch yapma
    if (userRole !== "admin") {
      return;
    }
    
    // İlk yüklemede veri çek
    fetchData();
    
    // Sayfa görünürken otomatik yenileme (30 saniyede bir)
    let interval = null;
    
    const handleVisibilityChange = () => {
      // Sayfa görünürse interval başlat, görünmezse durdur
      if (document.visibilityState === 'visible') {
        // Sayfa tekrar görünür olduğunda hemen güncelle
        fetchData();
        // Sonra düzenli güncellemeye devam et
        interval = setInterval(fetchData, 30000);
      } else {
        // Sayfa görünmez olduğunda interval'i temizle
        if (interval) {
          clearInterval(interval);
          interval = null;
        }
      }
    };
    
    // İlk durumda sayfa görünürse interval başlat
    if (document.visibilityState === 'visible') {
      interval = setInterval(fetchData, 30000);
    }
    
    // Visibility change event listener ekle
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup: component unmount olduğunda veya sayfa kapanınca temizle
    return () => {
      if (interval) {
        clearInterval(interval);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchData, userRole]);

  // Admin değilse sayfayı gösterme
  if (userRole !== "admin") {
    return (
      <Box p={4}>
        <Text>Yükleniyor...</Text>
      </Box>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "healthy":
        return "green";
      case "warning":
        return "yellow";
      case "critical":
        return "red";
      default:
        return "gray";
    }
  };

  // Grafik seçenekleri
  const getChartOptions = (timestamps) => ({
    chart: {
      type: "line",
      height: 200,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: {
      curve: "smooth",
      width: 2,
    },
    xaxis: {
      categories: timestamps,
      labels: { show: false },
    },
    yaxis: {
      min: 0,
      max: 100,
      labels: {
        formatter: (val) => `${val.toFixed(0)}%`,
      },
    },
    legend: {
      show: true,
      position: "top",
    },
    colors: ["#3182CE", "#38A169", "#D69E2E"],
    tooltip: {
      y: {
        formatter: (val) => `${val.toFixed(2)}%`,
      },
    },
  });

  const getChartSeries = (history) => [
    {
      name: "CPU",
      data: history.cpu,
    },
    {
      name: "Memory",
      data: history.memory,
    },
    {
      name: "Disk",
      data: history.disk,
    },
  ];

  if (loading) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }} px={4}>
        <Flex justify="center" align="center" minH="400px">
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" />
            <Text color={textColor}>Sistem bilgileri yükleniyor...</Text>
          </VStack>
        </Flex>
      </Box>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} px={4} pb={8}>
      <VStack spacing={6} align="stretch">
        {/* Başlık, Sunucu Seçici ve Yenile Butonu */}
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
          <Text fontSize="2xl" fontWeight="bold" color="white">
            Sunucu Yönetimi
          </Text>
          <HStack spacing={3}>
            <Select
              value={selectedServer}
              onChange={(e) => setSelectedServer(e.target.value)}
              size="sm"
              width={{ base: "150px", md: "200px" }}
              bg={cardBg}
              color={textColor}
            >
              <option value="admin">Admin Sunucusu</option>
              <option value="traccar">Traccar Sunucusu</option>
            </Select>
            <Button
              leftIcon={<FiRefreshCw />}
              onClick={fetchData}
              isLoading={refreshing}
              colorScheme="blue"
              size="sm"
            >
              Yenile
            </Button>
          </HStack>
        </Flex>

        {/* Seçilen Sunucu Kartı */}
        {selectedServer === "admin" ? (
          <ServerCard
            title="Admin Sunucusu"
            metrics={adminMetrics}
            health={adminHealth}
            metricsHistory={adminMetricsHistory}
            cardBg={cardBg}
            textColor={textColor}
            borderColor={borderColor}
            getStatusColor={getStatusColor}
            getChartOptions={getChartOptions}
            getChartSeries={getChartSeries}
          />
        ) : (
          <ServerCard
            title="Traccar Sunucusu"
            metrics={traccarMetrics}
            health={traccarHealth}
            metricsHistory={{ cpu: [], memory: [], disk: [], timestamps: [] }}
            cardBg={cardBg}
            textColor={textColor}
            borderColor={borderColor}
            getStatusColor={getStatusColor}
            getChartOptions={getChartOptions}
            getChartSeries={getChartSeries}
            isPlaceholder={true}
          />
        )}

        {/* Yasaklı IP'ler - Sadece Admin sunucusu için */}
        {selectedServer === "admin" && (
          <Box bg={cardBg} borderRadius="lg" p={6} boxShadow="md" borderWidth="1px" borderColor={borderColor}>
            <HStack mb={4}>
              <Icon as={FiShield} color="red.500" boxSize={5} />
              <Text fontSize="lg" fontWeight="bold" color={textColor}>
                Yasaklı IP'ler
              </Text>
              <Badge colorScheme="red" ml="auto">
                {bannedIPs.filter((ip) => ip.is_active).length} Aktif
              </Badge>
            </HStack>
            <Box maxH="400px" overflowY="auto">
              {bannedIPs.length === 0 ? (
                <Text color="gray.500" textAlign="center" py={4}>
                  Yasaklı IP bulunmuyor
                </Text>
              ) : (
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>IP Adresi</Th>
                      <Th>Durum</Th>
                      <Th>Sebep</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {bannedIPs.slice(0, 10).map((ip) => (
                      <Tr key={ip.id}>
                        <Td>
                          <Code fontSize="xs">{ip.ip_address}</Code>
                        </Td>
                        <Td>
                          <Badge colorScheme={ip.is_active ? "red" : "gray"} size="sm">
                            {ip.is_active ? "Aktif" : "Pasif"}
                          </Badge>
                        </Td>
                        <Td>
                          <Text fontSize="xs" color="gray.500" noOfLines={1}>
                            {ip.reason || "-"}
                          </Text>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </Box>
          </Box>
        )}

        {/* Log Görüntüleme - Sadece Admin sunucusu için */}
        {selectedServer === "admin" && (
          <Box bg={cardBg} borderRadius="lg" p={6} boxShadow="md" borderWidth="1px" borderColor={borderColor}>
            <HStack mb={4}>
              <Icon as={FiFileText} color="gray.500" boxSize={5} />
              <Text fontSize="lg" fontWeight="bold" color={textColor}>
                Sistem Log'ları
              </Text>
            </HStack>
            <Tabs>
              <TabList>
                <Tab>Hata Log'ları</Tab>
                <Tab>Erişim Log'ları</Tab>
              </TabList>
              <TabPanels>
                <TabPanel>
                  {errorLogs && errorLogs.exists ? (
                    <Box>
                      <Text fontSize="sm" color="gray.500" mb={2}>
                        Son {errorLogs.lines.length} satır (Toplam: {errorLogs.total_lines})
                      </Text>
                      <Box
                        bg="gray.900"
                        color="gray.100"
                        p={4}
                        borderRadius="md"
                        maxH="400px"
                        overflowY="auto"
                        fontFamily="mono"
                        fontSize="xs"
                      >
                        {errorLogs.lines.map((line, idx) => (
                          <Text key={idx} mb={1}>
                            {line}
                          </Text>
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    <Text color="gray.500">Log dosyası bulunamadı</Text>
                  )}
                </TabPanel>
                <TabPanel>
                  {accessLogs && accessLogs.exists ? (
                    <Box>
                      <Text fontSize="sm" color="gray.500" mb={2}>
                        Son {accessLogs.lines.length} satır (Toplam: {accessLogs.total_lines})
                      </Text>
                      <Box
                        bg="gray.900"
                        color="gray.100"
                        p={4}
                        borderRadius="md"
                        maxH="400px"
                        overflowY="auto"
                        fontFamily="mono"
                        fontSize="xs"
                      >
                        {accessLogs.lines.map((line, idx) => (
                          <Text key={idx} mb={1}>
                            {line}
                          </Text>
                        ))}
                      </Box>
                    </Box>
                  ) : (
                    <Text color="gray.500">Log dosyası bulunamadı</Text>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        )}
      </VStack>
    </Box>
  );
}

// Metric Card Component
function MetricCard({ label, value, subtitle, icon: Icon, color, percent, cardBg, textColor }) {
  const borderColor = useColorModeValue("gray.200", "gray.600");

  return (
    <Box
      bg={cardBg}
      borderRadius="lg"
      p={6}
      boxShadow="md"
      borderWidth="1px"
      borderColor={borderColor}
    >
      <Flex justify="space-between" align="start" mb={3}>
        <VStack align="start" spacing={1}>
          <Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase">
            {label}
          </Text>
          <Text fontSize="2xl" fontWeight="bold" color={textColor}>
            {value}
          </Text>
          {subtitle && (
            <Text fontSize="xs" color="gray.500">
              {subtitle}
            </Text>
          )}
        </VStack>
        <Box
          borderRadius="lg"
          bg={`${color}20`}
          p={3}
          color={color}
        >
          <Icon size={24} />
        </Box>
      </Flex>
      {percent !== undefined && (
        <Progress
          value={percent}
          colorScheme={percent > 90 ? "red" : percent > 70 ? "yellow" : "green"}
          size="sm"
          borderRadius="full"
        />
      )}
    </Box>
  );
}

// Server Card Component
function ServerCard({ 
  title, 
  metrics, 
  health, 
  metricsHistory, 
  cardBg, 
  textColor, 
  borderColor, 
  getStatusColor, 
  getChartOptions, 
  getChartSeries,
  isPlaceholder = false 
}) {
  return (
    <Box bg={cardBg} borderRadius="lg" p={6} boxShadow="md" borderWidth="1px" borderColor={borderColor}>
      <HStack mb={4}>
        <Icon as={FiServer} color="blue.500" boxSize={5} />
        <Text fontSize="lg" fontWeight="bold" color={textColor}>
          {title}
        </Text>
        {health && (
          <Badge colorScheme={getStatusColor(health.status)} ml="auto" fontSize="sm">
            {health.status === "healthy" ? "Sağlıklı" : health.status === "warning" ? "Uyarı" : "Kritik"}
          </Badge>
        )}
      </HStack>

      {isPlaceholder ? (
        <VStack spacing={4} py={8}>
          <Text color="gray.500" textAlign="center">
            Traccar sunucu metrikleri yakında eklenecek
          </Text>
        </VStack>
      ) : (
        <>
          {/* Sistem Metrikleri Kartları */}
          {metrics && (
            <SimpleGrid columns={{ sm: 2 }} spacing={4} mb={4}>
              <MetricCard
                label="CPU Kullanımı"
                value={`${metrics.cpu_percent.toFixed(1)}%`}
                icon={FiCpu}
                color="blue.500"
                percent={metrics.cpu_percent}
                cardBg={cardBg}
                textColor={textColor}
              />
              <MetricCard
                label="Memory Kullanımı"
                value={`${metrics.memory_percent.toFixed(1)}%`}
                subtitle={`${metrics.memory_used_gb.toFixed(1)} GB / ${metrics.memory_total_gb.toFixed(1)} GB`}
                icon={FiActivity}
                color="green.500"
                percent={metrics.memory_percent}
                cardBg={cardBg}
                textColor={textColor}
              />
              <MetricCard
                label="Disk Kullanımı"
                value={`${metrics.disk_percent.toFixed(1)}%`}
                subtitle={`${metrics.disk_used_gb.toFixed(1)} GB / ${metrics.disk_total_gb.toFixed(1)} GB`}
                icon={FiHardDrive}
                color="orange.500"
                percent={metrics.disk_percent}
                cardBg={cardBg}
                textColor={textColor}
              />
              <MetricCard
                label="Uptime"
                value={metrics.uptime_human}
                subtitle={`${Math.floor(metrics.uptime_seconds / 86400)} gün`}
                icon={FiServer}
                color="purple.500"
                cardBg={cardBg}
                textColor={textColor}
              />
            </SimpleGrid>
          )}

          {/* Metrik Grafikleri */}
          {metricsHistory.timestamps.length > 0 && (
            <Box mt={4}>
              <Text fontSize="sm" fontWeight="semibold" mb={2} color={textColor}>
                Metrik Trendi
              </Text>
              <Chart 
                options={getChartOptions(metricsHistory.timestamps)} 
                series={getChartSeries(metricsHistory)} 
                type="line" 
                height={200} 
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
